// Story layer: the elaboration shown in the drawer — why each model fits
// its problem, a peek at real-looking dataset rows, and a plain-English
// note on what preprocessing actually does. Pure content, no logic.

// ---- why THIS model for THIS problem (keyed by algorithm id) ----
export const WHY_HERE: Record<string, string> = {
  'churn-xgb':
    'Churn lives in a spreadsheet — rows of customers, columns of facts. XGBoost builds hundreds of small decision trees, each one correcting the previous one’s mistakes, which makes it the go-to choice for exactly this kind of table data. Bonus: it ranks which columns mattered, so the business learns why customers leave — not just who.',
  'churn-rf':
    'A Random Forest asks hundreds of randomized decision trees to vote. It is hard to overfit and needs almost no tuning — the solid baseline you build first, then try to beat. Compare with the XGBoost tab: same question, slightly different answer.',
  'fraud-iso':
    'Only ~1 in 600 payments is fraud, and thieves keep inventing new tricks. So instead of teaching the model what fraud looks like, Isolation Forest learns what NORMAL looks like and flags whatever separates from the crowd too easily. It needs no fraud labels at all.',
  'fraud-xgb':
    'When you do have labeled past fraud, a supervised model like XGBoost usually beats an anomaly detector — it learns the specific fingerprints of confirmed fraud instead of just “weirdness”. Compare both tabs: that is a real design decision fraud teams make.',
  'equip-rf':
    'Factory sensors drift, spike and glitch. A Random Forest handles noisy readings gracefully — one weird value cannot dominate hundreds of voting trees. Robust and explainable, which is why plant engineers trust it.',
  'intrusion-gb':
    'Network logs are structured data with mixed numbers and categories (bytes, counts, service type). Gradient Boosting shines there and outputs sharp probability scores — exactly what a security dashboard needs to rank thousands of alerts.',
  'demand-xgb':
    'Next week’s demand depends on last week, the same week last year, and whether a promotion runs — “lag features” in a table. XGBoost eats lag features for breakfast; this is what most retail forecasting teams actually ship.',
  'recommend-mf':
    'There is no spreadsheet of features here — just who rated what. Matrix Factorization discovers hidden “taste dimensions” from the ratings grid itself. It is the granddaddy of the embeddings behind Netflix and Spotify.',
  'house-xgb':
    'A house price is a number driven by dozens of interacting details — size × quality × age × garage. XGBoost captures those interactions automatically and reports which detail moved the price, which is exactly what an appraiser wants to see.',
  'credit-lr':
    'A bank must explain every rejection to the customer and the regulator. Logistic Regression’s answer is a simple weighted sum you can print on paper — auditable and fair to challenge. That is why it still runs real credit desks.',
  'credit-xgb':
    'More accurate than Logistic Regression, but much harder to explain — the classic accuracy-vs-explainability trade-off. Flip between the two tabs and you are doing genuine model selection.',
  'crop-cnn':
    'Pixels are not table columns. A convolutional network scans the photo with tiny pattern detectors — edges, then spots, then lesion shapes — which is literally how computers learned to “see”. A small MobileNet like this runs fine on a laptop.',
  'energy-xgb':
    'Electricity load follows temperature and time-of-day in sharp non-linear jumps (air-conditioning kicks in above 30 °C). Boosted trees capture those breakpoints; grid operators run models like this every single hour.',
}

// ---- a peek at the dataset (keyed by problem id) ----
export interface SampleTable {
  columns: string[]
  rows: (string | number)[][]
  note: string // one line under the table
}

export const SAMPLES: Record<string, SampleTable> = {
  churn: {
    columns: ['tenure', 'monthly $', 'contract', 'support calls', 'churned?'],
    rows: [
      [2, 85, 'Month-to-month', 5, 'Yes'],
      [48, 60, 'Two year', 0, 'No'],
      [7, 99, 'Month-to-month', 3, 'Yes'],
      [30, 45, 'One year', 1, 'No'],
    ],
    note: '7,043 customers like these — the model studies who left and who stayed.',
  },
  fraud: {
    columns: ['amount $', 'hour', 'foreign card', 'txns/hour', 'fraud?'],
    rows: [
      [12, 14, 'no', 1, 'Legit'],
      [1890, 3, 'yes', 7, 'Fraud'],
      [45, 19, 'no', 2, 'Legit'],
      [950, 2, 'yes', 4, 'Fraud'],
    ],
    note: '284,807 payments — only 492 are fraud. Finding them is the whole game.',
  },
  equipment: {
    columns: ['air temp K', 'torque Nm', 'rpm', 'tool wear min', 'failed?'],
    rows: [
      [298, 45, 1500, 20, 'Healthy'],
      [302, 70, 1350, 220, 'Failed'],
      [299, 40, 1550, 90, 'Healthy'],
      [301, 66, 1380, 190, 'Failed'],
    ],
    note: '10,000 sensor snapshots — worn tools under high torque fail first.',
  },
  intrusion: {
    columns: ['src bytes', 'dst bytes', 'conn count', 'service', 'attack?'],
    rows: [
      [230, 8150, 5, 'http', 'Benign'],
      [0, 0, 511, 'private', 'Attack'],
      [350, 1200, 8, 'smtp', 'Benign'],
      [6, 0, 480, 'private', 'Attack'],
    ],
    note: '125,973 connections — attacks hammer one port with hundreds of empty calls.',
  },
  demand: {
    columns: ['last week', 'same wk last yr', 'promo', 'week #', 'sold next wk'],
    rows: [
      [430, 410, 'no', 48, 419],
      [620, 580, 'yes', 50, 685],
      [250, 300, 'no', 23, 261],
      [510, 470, 'yes', 51, 602],
    ],
    note: '913,000 store×item weeks — history + promotions predict the next number.',
  },
  recommend: {
    columns: ['user avg ★', 'movie avg ★', 'genre match', '# rated', 'gave ★'],
    rows: [
      [3.8, 4.1, 0.7, 120, 4.0],
      [4.5, 4.7, 0.9, 300, 5.0],
      [2.9, 3.2, 0.3, 45, 3.0],
      [3.5, 4.6, 0.8, 210, 4.5],
    ],
    note: '100,000 ratings — the grid of who-liked-what is the only teacher.',
  },
  house: {
    columns: ['living sqft', 'quality /10', 'built', 'garage', 'sold for'],
    rows: [
      [1710, 7, 2003, 2, '$208,500'],
      [1262, 6, 1976, 2, '$181,500'],
      [2198, 9, 2007, 3, '$345,000'],
      [960, 5, 1955, 1, '$118,000'],
    ],
    note: '1,460 real sales from Ames, Iowa — 79 columns each; these 4 matter most.',
  },
  credit: {
    columns: ['income $/mo', 'loan $', 'history', 'employed yrs', 'repaid?'],
    rows: [
      [4500, 12000, 'good', 6, 'Repaid'],
      [2100, 30000, 'critical', 1, 'Default'],
      [6800, 9000, 'excellent', 12, 'Repaid'],
      [2800, 28000, 'delayed', 2, 'Default'],
    ],
    note: '1,000 past loans — the model learns which applications went bad.',
  },
  crop: {
    columns: ['photo', 'size', 'label'],
    rows: [
      ['leaf_0142.jpg', '224×224', 'Early Blight'],
      ['leaf_0968.jpg', '224×224', 'Healthy'],
      ['leaf_1204.jpg', '224×224', 'Late Blight'],
      ['leaf_0777.jpg', '224×224', 'Leaf Mold'],
    ],
    note: '54,305 labeled leaf photos — here the “rows” are pictures, not numbers.',
  },
  energy: {
    columns: ['temp °C', 'hour', 'weekend', 'load 24h ago', 'load MW'],
    rows: [
      [34, 18, 'no', '31,000', '39,700'],
      [22, 4, 'yes', '25,500', '24,100'],
      [40, 17, 'no', '36,000', '44,800'],
      [15, 12, 'no', '28,000', '29,300'],
    ],
    note: '145,366 hourly readings — heat + rush hour = the evening peak.',
  },
}

// ---- what preprocessing actually does here (keyed by problem id) ----
export const PREP_NOTES: Record<string, string> = {
  churn:
    'Fill the blank monthly charges with the typical value, turn contract words (“Month-to-month”…) into numbers, then hide 20% of customers — the model never sees them until the test.',
  fraud:
    'Scale the wildly different amounts onto one range so a $12 coffee and a $1,890 transfer are comparable, then set aside 20% of payments for the honest test.',
  equipment:
    'Smooth obvious sensor glitches, convert temperatures to one unit, and hold back 2,000 machines the model must diagnose blind.',
  intrusion:
    'Turn service names (http, ftp…) into numeric columns and cap extreme byte counts, then hide 20% of connections for the test.',
  demand:
    'Build the “lag” columns — last week’s sales, the same week last year — because the raw table only has dates and quantities. Hide the final weeks for the test.',
  recommend:
    'Re-index users and movies into a compact grid and subtract each user’s personal average (a grumpy 3★ ≈ a generous 4★), keeping 20% of ratings hidden.',
  house:
    'Fill missing garage years with the typical value, turn neighborhood names into numbers, and hide 292 houses — the model must price them unseen.',
  credit:
    'Encode credit-history words (“excellent”…“critical”) as ordered numbers and balance the classes so defaults are not drowned out; 200 loans stay hidden.',
  crop:
    'Resize every photo to 224×224 pixels, normalize colors, and randomly flip/rotate copies so the model cannot memorize exact pictures. 10% of photos stay hidden.',
  energy:
    'Extract hour-of-day and weekday from the timestamp, add yesterday-same-hour load as a column, and hold back the last month of readings for the test.',
}
